// src/pages/Withdraw/WithdrawPin.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { withdrawPinService } from '../../services/withdrawPinService';
import './WithdrawPin.css';

// ── Reusable 4-digit input group ─────────────────────────────────────────────
const PinInput = ({ label, value, onChange, hasError, disabled }) => {
  const inputRefs = useRef([]);

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (value[index] !== '') {
        const next = [...value];
        next[index] = '';
        onChange(next);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        const next = [...value];
        next[index - 1] = '';
        onChange(next);
      }
      return;
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleChange = (index, e) => {
    const raw = e.target.value.replace(/\D/g, ''); // digits only
    if (!raw) return;
    const digit = raw[raw.length - 1]; // take last typed digit
    const next = [...value];
    next[index] = digit;
    onChange(next);
    if (index < 3) inputRefs.current[index + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (!pasted) return;
    const next = ['', '', '', ''];
    pasted.split('').forEach((d, i) => { next[i] = d; });
    onChange(next);
    const focusIdx = Math.min(pasted.length, 3);
    inputRefs.current[focusIdx]?.focus();
  };

  return (
    <div>
      {label && <div className="pin-label">{label}</div>}
      <div className="pin-inputs">
        {value.map((digit, i) => (
          <input
            key={i}
            ref={(el) => (inputRefs.current[i] = el)}
            type="tel"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            disabled={disabled}
            onChange={(e) => handleChange(i, e)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            className={`pin-digit${digit ? ' filled' : ''}${hasError ? ' error-digit' : ''}`}
          />
        ))}
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const WithdrawPin = () => {
  const navigate = useNavigate();

  // 'loading' | 'create' | 'confirm_new' | 'enter'
  const [screen, setScreen] = useState('loading');

  const [pin, setPin]           = useState(['', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '']);
  const [tempPin, setTempPin]   = useState('');   // store first entry during create flow

  const [error, setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pinError, setPinError]   = useState(false);
  const [confirmError, setConfirmError] = useState(false);

  // ── On mount: check whether user already has a PIN ────────────────────────
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const data = await withdrawPinService.checkPinStatus();
        setScreen(data.has_pin ? 'enter' : 'create');
      } catch {
        setScreen('enter'); // safe fallback
      }
    };
    checkStatus();
  }, []);

  const pinString = (arr) => arr.join('');
  const pinComplete = (arr) => arr.every((d) => d !== '');

  const resetErrors = () => {
    setError('');
    setPinError(false);
    setConfirmError(false);
  };

  // ── STEP 1 of create: user sets their new PIN ─────────────────────────────
  const handleCreateSubmit = async () => {
    resetErrors();
    if (!pinComplete(pin)) {
      setError('Please enter all 4 digits');
      setPinError(true);
      return;
    }
    // Store it and move to confirmation step
    setTempPin(pinString(pin));
    setPin(['', '', '', '']);
    setSuccess('');
    setScreen('confirm_new');
  };

  // ── STEP 2 of create: user confirms their new PIN ────────────────────────
  const handleConfirmSubmit = async () => {
    resetErrors();
    if (!pinComplete(confirmPin)) {
      setError('Please enter all 4 digits');
      setConfirmError(true);
      return;
    }
    if (pinString(confirmPin) !== tempPin) {
      setError('PINs do not match. Please try again.');
      setConfirmError(true);
      setConfirmPin(['', '', '', '']);
      return;
    }

    setSubmitting(true);
    try {
      await withdrawPinService.setPin(tempPin, pinString(confirmPin));
      setSuccess('PIN created! Verifying…');
      // Immediately ask them to enter it to proceed
      setTimeout(() => {
        setSuccess('');
        setError('');
        setConfirmPin(['', '', '', '']);
        setPin(['', '', '', '']);
        setTempPin('');
        setScreen('enter');
      }, 1000);
    } catch (err) {
      setError(err.message || 'Failed to set PIN. Please try again.');
      setConfirmError(true);
      setConfirmPin(['', '', '', '']);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Enter existing PIN and proceed ────────────────────────────────────────
  const handleVerifySubmit = async () => {
    resetErrors();
    if (!pinComplete(pin)) {
      setError('Please enter all 4 digits');
      setPinError(true);
      return;
    }

    setSubmitting(true);
    try {
      await withdrawPinService.verifyPin(pinString(pin));
      // Mark PIN as verified in sessionStorage so re-visits within the
      // same session skip straight through (optional UX improvement)
      sessionStorage.setItem('withdraw_pin_verified', '1');
      navigate('/withdraw');
    } catch (err) {
      setError(err.message || 'Incorrect PIN. Please try again.');
      setPinError(true);
      setPin(['', '', '', '']);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading state ──────────────────────────────────────────────────────────
  if (screen === 'loading') {
    return (
      <div className="pin-page">
        <div className="pin-container">
          <div className="pin-card">
            <div className="pin-icon">🔐</div>
            <p>Checking your account…</p>
          </div>
        </div>
      </div>
    );
  }

  // ── CREATE PIN — Step 1 ───────────────────────────────────────────────────
  if (screen === 'create') {
    return (
      <div className="pin-page">
        <div className="pin-header">
          <button className="pin-back-link" onClick={() => navigate('/dashboard')}>
            ← Back to Dashboard
          </button>
        </div>
        <div className="pin-container">
          <div className="pin-card">
            <div className="pin-icon">🔒</div>
            <h1>Create Withdrawal PIN</h1>
            <p>Set a 4-digit PIN to secure your withdrawals. You'll need this every time you withdraw.</p>

            {/* Step dots */}
            <div className="pin-steps">
              <div className="pin-step-dot active" />
              <div className="pin-step-dot" />
            </div>

            <PinInput
              label="Enter new PIN"
              value={pin}
              onChange={(v) => { setPin(v); resetErrors(); }}
              hasError={pinError}
              disabled={submitting}
            />

            {error && <div className="pin-error">⚠️ {error}</div>}

            <button
              className={`pin-submit-btn${submitting ? ' loading' : ''}`}
              onClick={handleCreateSubmit}
              disabled={submitting || !pinComplete(pin)}
            >
              {submitting ? '' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── CREATE PIN — Step 2 (confirm) ─────────────────────────────────────────
  if (screen === 'confirm_new') {
    return (
      <div className="pin-page">
        <div className="pin-header">
          <button
            className="pin-back-link"
            onClick={() => { setScreen('create'); setPin(['', '', '', '']); resetErrors(); }}
          >
            ← Back
          </button>
        </div>
        <div className="pin-container">
          <div className="pin-card">
            <div className="pin-icon">✅</div>
            <h1>Confirm Your PIN</h1>
            <p>Re-enter your 4-digit PIN to confirm it.</p>

            {/* Step dots */}
            <div className="pin-steps">
              <div className="pin-step-dot" />
              <div className="pin-step-dot active" />
            </div>

            <PinInput
              label="Confirm PIN"
              value={confirmPin}
              onChange={(v) => { setConfirmPin(v); resetErrors(); }}
              hasError={confirmError}
              disabled={submitting}
            />

            {error   && <div className="pin-error">⚠️ {error}</div>}
            {success && <div className="pin-success">✅ {success}</div>}

            <button
              className={`pin-submit-btn${submitting ? ' loading' : ''}`}
              onClick={handleConfirmSubmit}
              disabled={submitting || !pinComplete(confirmPin)}
            >
              {submitting ? '' : 'Set PIN'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── ENTER PIN (returning users + after create) ────────────────────────────
  return (
    <div className="pin-page">
      <div className="pin-header">
        <button className="pin-back-link" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
      </div>
      <div className="pin-container">
        <div className="pin-card">
          <div className="pin-icon">🔐</div>
          <h1>Enter Withdrawal PIN</h1>
          <p>Enter your 4-digit PIN to proceed with your withdrawal.</p>

          <PinInput
            value={pin}
            onChange={(v) => { setPin(v); resetErrors(); }}
            hasError={pinError}
            disabled={submitting}
          />

          {error && <div className="pin-error">⚠️ {error}</div>}

          <button
            className={`pin-submit-btn${submitting ? ' loading' : ''}`}
            onClick={handleVerifySubmit}
            disabled={submitting || !pinComplete(pin)}
          >
            {submitting ? '' : 'Proceed to Withdraw'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WithdrawPin;